import compose from 'ramda/es/compose';
import sort from 'ramda/es/sort';

type InventoryConfig = {
  [key: string]: string;
};
type Config = {
  low: Item;
  last: Item;
};
type StockEntry = [string, Item];
type StockMap = StockEntry[];
type Options = Partial<LowStockOptions>;

export interface LowStockElement extends HTMLElement {
  lowstock?: LowStock;
}

export interface LowStockOptions {
  configAttribute: string;
  inventoryAttribute: string;
  keySelector: string;
  visibleClass: string;
}

interface Item {
  enabled: string;
  message: string;
  threshold: string;
}

export class LowStock {
  static defaults = {
    configAttribute: '[data-stock-config]',
    inventoryAttribute: '[data-stock-inventory]',
    keySelector: 'data-stock-key',
    visibleClass: 'is-visible',
  };
  element: LowStockElement;
  options: LowStockOptions;
  enabledConfigs: StockMap;
  targets: HTMLElement[];
  optionsHandler: EventListener;
  inventory: InventoryConfig;

  constructor(element: LowStockElement, options: Options = {}) {
    if (!element || element.lowstock) {
      return;
    }

    this.options = {
      ...LowStock.defaults,
      ...options,
    };

    element.lowstock = this;
    this.element = element;

    const configEl = document.querySelector(this.options.configAttribute);
    const inventoryEl = this.element.querySelector(
      this.options.inventoryAttribute,
    );

    if (!configEl || !inventoryEl || !this.element) {
      return;
    }

    this.inventory = JSON.parse(inventoryEl.innerHTML);

    const config = JSON.parse(configEl.innerHTML) as Config;
    const configEntries = Object.entries(config);

    this.enabledConfigs = compose(
      this.getEnabled,
      sort(this.sortByThreshold),
    )(configEntries);

    this.targets = Array.from(
      this.element.querySelectorAll(`[${this.options.keySelector}]`),
    );

    this.bind();
  }

  bind() {
    this.optionsHandler = this.handleOptionsChange.bind(this) as EventListener;
    this.element.addEventListener('optionsChange', this.optionsHandler);
  }

  getEnabled(arr: StockMap) {
    return arr.filter(([, item]) => item.enabled === 'true');
  }

  sortByThreshold(x: StockEntry, y: StockEntry): number {
    const [, curr] = x;
    const [, next] = y;

    return parseInt(curr.threshold, 10) - parseInt(next.threshold, 10);
  }

  handleOptionsChange(event: CustomEvent) {
    const {variant = ''} = event.detail;

    this.hideKeys();

    if (!variant) {
      return;
    }

    const quantity = this.inventory[variant.id];
    const key = this.getKeyByQuantity(parseInt(quantity, 10));

    if (!key) {
      return;
    }

    this.showKey(key);
  }

  hideKeys() {
    this.targets.forEach(item =>
      item.classList.remove(this.options.visibleClass),
    );
  }

  showKey(key: string) {
    const match = this.element.querySelector(
      `[${this.options.keySelector}='${key}']`,
    );

    if (match) {
      match.classList.add(this.options.visibleClass);
    }
  }

  getKeyByQuantity(quantity: number): string {
    const result = this.enabledConfigs.find(([, data]) => {
      const threshold = parseInt(data.threshold, 10);

      return quantity <= threshold && quantity !== 0;
    });

    if (result) {
      return result[0];
    }

    return '';
  }

  destroy() {
    this.element.lowstock = undefined;
    this.element.removeEventListener('optionsChange', this.optionsHandler);
  }
}
